<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>{{ $spinnerText }}</span>
            </span>
        </div>
    </Transition>

    <ConfirmDialog header="Confirm delete"
                   question="Do you want to delete selected record?"
                   v-model="isConfirmationOpen"
                   :operation="deleteData" />

    <div class="w-100">
        <hr style="margin-bottom:20px">
        <div v-if="editMode || addMode"
             class="flex justify-content-end gap-2">
            <slot v-if="addMode"
                  name="before-in-add"
                  :saveData="saveData"
                  :closeForm="closeForm"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <slot v-if="editMode"
                  name="before-in-edit"
                  :saveData="saveData"
                  :deleteData="deleteData"
                  :closeForm="closeForm"
                  :deleteWithConfirmation="openConfirmationWindow"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <slot v-if="editMode || addMode"
                  name="before-in-add-edit"
                  :saveData="saveData"
                  :deleteData="deleteData"
                  :closeForm="closeForm"
                  :deleteWithConfirmation="openConfirmationWindow"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <Button @click="() => saveData(showAfterSave)"
                    :disabled="sourceForm.processing"
                    severity="success"
                    class="bg-secondary-500 hover:bg-gray-200 text-white hover:text-gray-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2">
                <span class="flex items-center gap-1"><i class="fa-regular fa-floppy-disk text-sm"></i><span>{{
                    __(saveButtonName) }}</span></span>
            </Button>
            <Button v-if="editMode && (!buttonsVisibility || (!buttonsVisibility?.deny?.includes('delete') && (buttonsVisibility?.allow?.includes('delete') || unprotected.includes(formName + '.delete') || hasPermission([formName, 'delete']))))"
                    @click="openConfirmationWindow"
                    :disabled="sourceForm.processing"
                    :unstyled="true"
                    class="bg-white hover:bg-rose-50 text-rose-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-rose-200 transition-colors duration-150 flex items-center gap-2"
                    severity="danger"><span class="flex items-center gap-1"><i class="fa-regular fa-trash-can text-sm"></i><span>{{
                        __('Delete')
                    }}</span></span>
            </Button>

            <Button @click="cancelEdit"
                    :disabled="sourceForm.processing"
                    severity="secondary"
                    :unstyled="true"
                    class="bg-white hover:bg-gray-200 text-gray-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2">
                {{ __('Cancel') }}
            </Button>
            <slot v-if="addMode"
                  name="after-in-add"
                  :saveData="saveData"
                  :closeForm="closeForm"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <slot v-if="editMode"
                  name="after-in-edit"
                  :saveData="saveData"
                  :deleteData="deleteData"
                  :closeForm="closeForm"
                  :deleteWithConfirmation="openConfirmationWindow"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <slot v-if="editMode || addMode"
                  name="after-in-add-edit"
                  :saveData="saveData"
                  :deleteData="deleteData"
                  :closeForm="closeForm"
                  :deleteWithConfirmation="openConfirmationWindow"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
        </div>
        <div v-else
             class="flex justify-content-end gap-2">
            <slot name="before-in-show"
                  :editData="editData"
                  :closeForm="closeForm"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
            <Button v-if="!buttonsVisibility || (!buttonsVisibility?.deny?.includes('edit') && (buttonsVisibility?.allow?.includes('edit') || unprotected.includes(formName, '.edit') || hasPermission([formName, 'edit'])))"
                    @click="editData"
                    :disabled="sourceForm.processing"
                    style="margin:0 5px"
                    severity="warning"
                    class="bg-secondary-500 hover:bg-gray-200 text-white hover:text-gray-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2">
                {{ __('Edit') }}
            </Button>
            <Button @click="closeForm"
                    :disabled="sourceForm.processing"
                    style="margin:0 5px"
                    severity="secondary"
                    class="bg-white hover:bg-gray-200 text-gray-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2">
                {{ __('Back') }}
            </Button>
            <slot name="after-in-show"
                  :editData="editData"
                  :closeForm="closeForm"
                  :sourceForm="sourceForm"
                  :formProps="formProps"></slot>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useForm, router } from '@inertiajs/vue3';
import Button from 'primevue/button';
import ConfirmDialog from '@pages/Crud/ConfirmDialog.vue'
import { setFormState, validMsgHeaderRemove, validMsgFieldRemove, validMsgHeaderAdd, validMsgFieldAdd, onErrorService } from '@pages/Helper/crud_utils.js'
import { __, screenMessage, hasPermission, convertDate } from '@pages/Helper/utils.js'

const props = defineProps({
    formProps: Object,
    sourceForm: Object,
    uploadedFiles: Object,
    buttonsVisibility: {
        type: Object,
        default: undefined,
    },
    showAfterSave: { type: Boolean, default: false },
    beforeSave: { type: Function, default: null },
    afterSave: { type: Function, default: null },
    validation: { type: Function, default: null },
    saveButtonName: { type: String, default: 'Save' },
    cancelButtonName: { type: String, default: 'Cancel' },
    deleteButtonName: { type: String, default: 'Delete' },
    editButtonName: { type: String, default: 'Edit' },
    backButtonName: { type: String, default: 'Back' },
})

const formName = props.formProps.formName;
const unprotected = props.formProps.unprotected ?? [];
const isFormDisabled = defineModel('isFormDisabled')
const autoSave = defineModel('autoSave')
const isConfirmationOpen = ref(false)
const isLoading = ref(false)

const editMode = ref(props.formProps.editMode)
const addMode = ref(props.formProps.addMode)

// -----------------------------------------------------------------------------------------------------------

setFormState(isFormDisabled, editMode, addMode)

// -----------------------------------------------------------------------------------------------------------

watch(() => autoSave.value, (newValue, oldValue) => {
    if (newValue) {
        saveData()
    }
}, { deep: true, immediate: true })

// -----------------------------------------------------------------------------------------------------------

const saveData = async (showAfterSave = false, modeWhenShowAfterSave = 'show') => {
    isLoading.value = true
    const routeName = editMode.value ? props.formProps.routes.update : props.formProps.routes.store
    let sourceForm = prepareFiles(props.sourceForm)

    for (let field in sourceForm) {
        if (sourceForm[field] instanceof Date) {
            sourceForm[field] = convertDate(sourceForm[field])
        }
        if (field === 'log' && Array.isArray(sourceForm[field])) {
            delete sourceForm[field]
        }
        if (field === 'parent_id' && typeof sourceForm[field] === 'object' && checkTreeObject(sourceForm[field])) {
            sourceForm[field] = parseInt(Object.keys(sourceForm[field])[0])
        }
    }

    if (props.beforeSave) {
        sourceForm = await props.beforeSave(sourceForm)
        if (sourceForm === false) {
            isLoading.value = false
            return false;
        }
    }

    useForm(sourceForm).post(route(routeName), {
        preserveState: true,
        preserveScroll: true,
        forceFormData: true,
        onBefore: (visit) => {
            return onBeforeService(visit, sourceForm)
        },
        onError: (errors, errorBag) => {
            // Obsługa błędu 413 - Content Too Large
            if (errorBag?.response?.status === 413) {
                validMsgHeaderRemove()
                validMsgHeaderAdd(__('Uploaded data are too large'))
                return;
            }
            onErrorService(errors, formName)
        },
        onSuccess: () => {
            if (showAfterSave) {
                cancelEditAndShow(modeWhenShowAfterSave)
            } else {
                cancelEdit()
            }
        },
        onFinish: () => {
            isLoading.value = false
            if (props.afterSave) {
                props.afterSave()
            }
            props.formProps.getLazyData()
        }
    })
}

// -----------------------------------------------------------------------------------------------------------

const checkTreeObject = (obj) => {
    if (obj === undefined || obj === null) {
        return false;
    }

    const keys = Object.keys(obj);

    if (keys.length !== 1) {
        return false;
    }

    const key = keys[0];
    const value = obj[key];

    return !isNaN(key) && typeof value === 'boolean';
}

// -----------------------------------------------------------------------------------------------------------

const onBeforeService = (visit, sourceForm) => {
    Object.keys(visit.data).forEach(key => {
        if (visit.data[key] === null && addMode.value === true) {
            delete visit.data[key];
        }
    });

    if (props.validation) {
        const isValid = props.validation(sourceForm, formName)
        if (!isValid) {
            isLoading.value = false
        }
        return isValid
    }
};


// -----------------------------------------------------------------------------------------------------------

const prepareFiles = (sourceForm) => {
    if (sourceForm?.files !== undefined) {
        sourceForm.files = { ...sourceForm.files ?? [] }
    }
    for (let key in sourceForm) {
        if (Array.isArray(sourceForm[key])) {
            sourceForm[key].forEach(elem => prepareFiles(elem))
        }
    }
    return sourceForm
}

// -----------------------------------------------------------------------------------------------------------

const deleteData = () => {
    router.post(route(props.formProps.routes.delete), { 'selectedToDelete': [props.sourceForm?.id] }, {
        preserveState: true,
        preserveScroll: true,
        onError: (errors) => {
            console.log(errors)
            isLoading.value = false
        },
        onFinish: () => {
            isLoading.value = false
            props.formProps.getLazyData()
            cancelEdit()
        }
    })
}

// -----------------------------------------------------------------------------------------------------------

const editData = () => {
    editMode.value = true
    setFormState(isFormDisabled, editMode, addMode)
}

// -----------------------------------------------------------------------------------------------------------

const openConfirmationWindow = () => {
    isConfirmationOpen.value = true
}

// -----------------------------------------------------------------------------------------------------------

const cancelEdit = () => {
    //screenMessage()
    if (editMode.value || addMode.value) {
        closeForm()
    }
}

// -----------------------------------------------------------------------------------------------------------

const cancelEditAndShow = (modeWhenShowAfterSave) => {
    //screenMessage()
    if (modeWhenShowAfterSave == 'show') {
        editMode.value = false;
        isFormDisabled.value = true;
    }
    addMode.value = false
}

// -----------------------------------------------------------------------------------------------------------

const closeForm = () => {
    props.formProps.closeModal()
}

// -----------------------------------------------------------------------------------------------------------

</script>

<style lang="scss" scoped>
.error-message-header {
    color: red !important
}
</style>
